import type { QueryClient } from '@tanstack/react-query';
import {
  isTerminalRunStatus,
  type ProcessRunDetailDto,
  type RunNodeStatus,
  type RunStatus,
} from '@api/processRuns';
import type { ProcessArtifactDto } from '@api/processes';
import type { SseEvent } from '@hooks/useSseStream';
import { processDetailQueryKey } from '../../hooks/useProcessDetail';
import { processRunsListQueryKey } from '../../hooks/processRunsQueryKeys';
import { preserveTranscripts, seedFromRunDetail, terminalOutcome } from './state';
import type {
  ManualWaiting,
  NodeLiveState,
  ProcessRunSessionState,
  RunnerWaiting,
  SafeSetState,
  TranscriptEntry,
} from './types';

const RUN_STATUS_VALUES: ReadonlySet<RunStatus> = new Set<RunStatus>([
  'pending', 'running', 'waiting_for_action', 'completed', 'failed', 'cancelled',
]);

const RUN_NODE_STATUS_VALUES: ReadonlySet<RunNodeStatus> = new Set<RunNodeStatus>([
  'pending', 'provisioning', 'running', 'waiting_for_runner', 'waiting_for_user',
  'completed', 'failed', 'skipped',
]);

/** Statuses that mean the run has not put this node to work (yet, or ever). */
const NOT_STARTED: ReadonlySet<RunNodeStatus> = new Set<RunNodeStatus>([
  'pending', 'skipped',
]);

const isRunStatus = (raw: unknown): raw is RunStatus =>
  typeof raw === 'string' && RUN_STATUS_VALUES.has(raw as RunStatus);

const isRunNodeStatus = (raw: unknown): raw is RunNodeStatus =>
  typeof raw === 'string' && RUN_NODE_STATUS_VALUES.has(raw as RunNodeStatus);

interface EventHandlerDeps {
  readonly processId: string | undefined;
  readonly queryClient: QueryClient;
  readonly safeSetState: SafeSetState;
  readonly abortStream: () => void;
  readonly abortReconnect: () => void;
  /**
   * Given a gate nodeId and the chosen decision, return the node ids
   * reachable exclusively through the unchosen branch. The event handler
   * marks them as `skipped` so the canvas immediately reflects the
   * decision's downstream effect. Returns an empty array when the graph
   * is unavailable or the gate has no unchosen branch edges.
   */
  readonly unchosenBranchNodeIds: (
    gateNodeId: string,
    decision: string,
  ) => readonly string[];
}

function invalidateProcessQueries(
  processId: string | undefined,
  queryClient: QueryClient,
): void {
  if (!processId) return;
  queryClient
    .invalidateQueries({ queryKey: processDetailQueryKey(processId) })
    .catch(() => undefined);
  queryClient
    .invalidateQueries({ queryKey: processRunsListQueryKey(processId) })
    .catch(() => undefined);
}

function handleReconnected(
  event: SseEvent,
  safeSetState: SafeSetState,
): void {
  const detail = event.data as unknown as ProcessRunDetailDto | null | undefined;
  if (!detail?.run) return;
  const seeded = seedFromRunDetail(detail);
  safeSetState((prev) => {
    if (prev.active && prev.active.mode.runId !== detail.run.runId) {
      return prev;
    }
    const nextActive = prev.active
      ? { ...prev.active, runStatus: seeded.runStatus }
      : null;
    return {
      ...prev,
      active: nextActive,
      perNode: preserveTranscripts(prev.perNode, seeded.perNode),
      gateWaiting: seeded.gateWaiting,
      runnerWaiting: seeded.runnerWaiting,
      manualWaiting: seeded.manualWaiting,
    };
  });
}

function handleRunStatus(
  event: SseEvent,
  deps: EventHandlerDeps,
): void {
  const status = (event.data as { status?: unknown }).status;
  if (!isRunStatus(status)) return;
  deps.safeSetState((prev) => ({
    ...prev,
    active: prev.active ? { ...prev.active, runStatus: status } : prev.active,
  }));
  if (isTerminalRunStatus(status)) {
    invalidateProcessQueries(deps.processId, deps.queryClient);
    deps.safeSetState((prev) => ({
      ...prev,
      gateWaiting: null,
      runnerWaiting: null,
      manualWaiting: null,
      lastTerminalRun: {
        runId: prev.active?.mode.runId ?? null,
        outcome: terminalOutcome(status),
        // Carry the run metadata forward so the post-run footer keeps
        // the correct title ("Test run" vs "Run #N") without needing
        // a second fetch.
        runNumber: prev.active?.runNumber,
        isTest: prev.active?.isTest,
      },
    }));
  }
}

/** Next free slot in the observed start order (see {@link NodeLiveState.startedSeq}). */
function nextStartedSeq(perNode: ReadonlyMap<string, NodeLiveState>): number {
  let next = 0;
  for (const state of perNode.values()) {
    if (state.startedSeq !== undefined && state.startedSeq >= next) {
      next = state.startedSeq + 1;
    }
  }
  return next;
}

function handleNodeState(
  event: SseEvent,
  safeSetState: SafeSetState,
): void {
  const nodeId = (event.data as { nodeId?: unknown }).nodeId;
  const status = (event.data as { status?: unknown }).status;
  const iteration = (event.data as { iteration?: unknown }).iteration;
  const errorMsg = (event.data as { errorMessage?: unknown }).errorMessage;
  const errorDetail = (event.data as { errorDetail?: unknown }).errorDetail;
  const executionTimeMs = (event.data as { executionTimeMs?: unknown }).executionTimeMs;
  const creditsSpent = (event.data as { creditsSpent?: unknown }).creditsSpent;
  if (typeof nodeId !== 'string' || !isRunNodeStatus(status)) return;
  safeSetState((prev) => {
    const next = new Map(prev.perNode);
    const existing = next.get(nodeId);
    const resolvedIteration =
      typeof iteration === 'number' ? iteration : (existing?.iteration ?? 0);
    const isNewRun =
      status === 'running' &&
      (existing?.status !== 'running' || existing?.iteration !== resolvedIteration);
    next.set(nodeId, {
      status,
      iteration: resolvedIteration,
      startedSeq:
        existing?.startedSeq ??
        (NOT_STARTED.has(status) ? undefined : nextStartedSeq(prev.perNode)),
      errorMessage: typeof errorMsg === 'string' ? errorMsg : null,
      errorDetail: typeof errorDetail === 'string' ? errorDetail : null,
      executionTimeMs: typeof executionTimeMs === 'number' ? executionTimeMs : null,
      creditsSpent: typeof creditsSpent === 'number' ? creditsSpent : null,
      artifacts: isNewRun ? [] : (existing?.artifacts ?? []),
      transcript: isNewRun ? [] : (existing?.transcript ?? []),
    });
    const runnerWaiting = nextRunnerWaiting(prev, nodeId, status, resolvedIteration);
    const manualWaiting = nextManualWaiting(prev, nodeId, status, resolvedIteration);
    return { ...prev, perNode: next, runnerWaiting, manualWaiting };
  });
}

/**
 * Fold a node_state envelope into the {@code runnerWaiting} prompt: raise it
 * when a node parks in {@code waiting_for_runner}, clear it once that same
 * node moves on (claimed → running, or terminal).
 */
function nextRunnerWaiting(
  prev: ProcessRunSessionState,
  nodeId: string,
  status: RunNodeStatus,
  iteration: number,
): RunnerWaiting | null {
  if (status === 'waiting_for_runner') {
    const runId = prev.active?.mode.runId;
    return runId ? { runId, nodeId, iteration } : prev.runnerWaiting;
  }
  if (prev.runnerWaiting?.nodeId === nodeId) return null;
  return prev.runnerWaiting;
}

/**
 * Fold a node_state envelope into the {@code manualWaiting} prompt: raise it
 * when a node parks in {@code waiting_for_user}, clear it once that same node
 * moves on (completed, failed, or timed out back to another status).
 */
function nextManualWaiting(
  prev: ProcessRunSessionState,
  nodeId: string,
  status: RunNodeStatus,
  iteration: number,
): ManualWaiting | null {
  if (status === 'waiting_for_user') {
    const runId = prev.active?.mode.runId;
    return runId ? { runId, nodeId, iteration } : prev.manualWaiting;
  }
  if (prev.manualWaiting?.nodeId === nodeId) return null;
  return prev.manualWaiting;
}

function handleArtifact(
  event: SseEvent,
  safeSetState: SafeSetState,
): void {
  const nodeId = (event.data as { nodeId?: unknown }).nodeId;
  if (typeof nodeId !== 'string') return;
  const artifact = event.data as unknown as ProcessArtifactDto;
  safeSetState((prev) => {
    const next = new Map(prev.perNode);
    const existing = next.get(nodeId) ?? {
      status: 'running' as RunNodeStatus,
      iteration: 0,
      errorMessage: null,
      errorDetail: null,
      artifacts: [] as readonly ProcessArtifactDto[],
      transcript: [] as readonly TranscriptEntry[],
    };
    const idx = existing.artifacts.findIndex((a) => a.artifactId === artifact.artifactId);
    const updatedArtifacts = idx >= 0
      ? existing.artifacts.map((a, i) => (i === idx ? artifact : a))
      : [...existing.artifacts, artifact];
    next.set(nodeId, { ...existing, artifacts: updatedArtifacts });
    return { ...prev, perNode: next };
  });
}

function handleGateWaiting(
  event: SseEvent,
  safeSetState: SafeSetState,
): void {
  const runId = (event.data as { runId?: unknown }).runId;
  const nodeId = (event.data as { nodeId?: unknown }).nodeId;
  const iteration = (event.data as { iteration?: unknown }).iteration;
  if (
    typeof runId !== 'string' ||
    typeof nodeId !== 'string' ||
    typeof iteration !== 'number'
  ) {
    return;
  }
  safeSetState((prev) => ({
    ...prev,
    gateWaiting: { runId, nodeId, iteration },
  }));
}

function handleGateDecided(
  event: SseEvent,
  deps: EventHandlerDeps,
): void {
  const nodeId = (event.data as { nodeId?: unknown }).nodeId;
  const decision = (event.data as { decision?: unknown }).decision;
  const iteration = (event.data as { iteration?: unknown }).iteration;
  deps.safeSetState((prev) => {
    const next = new Map(prev.perNode);
    if (typeof nodeId === 'string') {
      const existing = next.get(nodeId);
      next.set(nodeId, {
        status: 'completed',
        iteration: typeof iteration === 'number'
          ? iteration
          : (existing?.iteration ?? 0),
        startedSeq: existing?.startedSeq,
        errorMessage: null,
        errorDetail: null,
        artifacts: existing?.artifacts ?? [],
        transcript: existing?.transcript ?? [],
      });
    }
    if (typeof nodeId === 'string' && typeof decision === 'string') {
      for (const skippedId of deps.unchosenBranchNodeIds(nodeId, decision)) {
        const ex = next.get(skippedId);
        if (!ex || ex.status === 'pending') {
          next.set(skippedId, {
            status: 'skipped',
            iteration: ex?.iteration ?? 0,
            startedSeq: ex?.startedSeq,
            errorMessage: null,
            errorDetail: null,
            artifacts: ex?.artifacts ?? [],
            transcript: ex?.transcript ?? [],
          });
        }
      }
    }
    return { ...prev, gateWaiting: null, perNode: next };
  });
}

function handleStreamEnd(deps: EventHandlerDeps): void {
  deps.abortStream();
  deps.abortReconnect();
  deps.safeSetState((prev) =>
    prev.streamDisconnected || prev.isReconnecting
      ? { ...prev, streamDisconnected: false, isReconnecting: false }
      : prev,
  );
  invalidateProcessQueries(deps.processId, deps.queryClient);
  deps.safeSetState((prev) => ({
    ...prev,
    active: null,
    gateWaiting: null,
    runnerWaiting: null,
    manualWaiting: null,
  }));
}

function handleNodeText(
  event: SseEvent,
  safeSetState: SafeSetState,
): void {
  const nodeId = (event.data as { nodeId?: unknown }).nodeId;
  const text = (event.data as { text?: unknown }).text;
  const timestamp = (event.data as { timestamp?: unknown }).timestamp;
  if (typeof nodeId !== 'string' || typeof text !== 'string') return;
  // Use the server-stamped timestamp when present — it's the only stable
  // identity a text delta carries, and we rely on it for the dedupe below.
  // Falling back to a client clock only happens for malformed envelopes,
  // where a duplicate would already be impossible to detect anyway.
  const ts = typeof timestamp === 'string'
    ? timestamp
    : new Date().toISOString();
  const entry: TranscriptEntry = { type: 'text', text, timestamp: ts };
  safeSetState((prev) => {
    const next = new Map(prev.perNode);
    // Stream ordering isn't guaranteed across SSE channels — a
    // node_text envelope can land BEFORE the first node_state for
    // that node (the worker emits the first text chunk in the same
    // tick it transitions the row to RUNNING). Returning early here
    // would drop those leading chunks; mirror `handleArtifact`'s
    // fallback so the dock still gets the full transcript.
    const existing = next.get(nodeId) ?? {
      status: 'running' as RunNodeStatus,
      iteration: 0,
      errorMessage: null,
      errorDetail: null,
      artifacts: [] as readonly ProcessArtifactDto[],
      transcript: [] as readonly TranscriptEntry[],
    };
    // Same dedupe pattern artifacts use (see `mergeArtifacts` /
    // "useProcessRunSession artifact dedup" tests). Per-node text deltas
    // have two known double-delivery sources: the in-process fast path on
    // the same replica fires concurrently with the cross-replica projector
    // reading the durable stream (the watermark dedupe between them has a
    // TOCTOU window), and a reconnected subscriber re-reads from the last
    // store cursor, which can sit behind events already delivered to the
    // previous subscriber on this client. Either path produces an exact
    // re-emission of the same (timestamp, text) pair, and without this
    // guard the visible transcript renders the same paragraph two or
    // three times — even though the persisted JSON on the node-state row
    // is clean, so a page reload silently "fixes" the screen. Identity is
    // `(timestamp, text)` rather than `timestamp` alone because Java's
    // `Instant.now()` resolves to millisecond precision on macOS and two
    // distinct deltas can legitimately share a millisecond.
    const isDup = existing.transcript.some(
      (e) => e.type === 'text' && e.timestamp === ts && e.text === text,
    );
    if (isDup) return prev;
    next.set(nodeId, {
      ...existing,
      transcript: [...existing.transcript, entry],
    });
    return { ...prev, perNode: next };
  });
}

function handleNodeTool(
  event: SseEvent,
  safeSetState: SafeSetState,
): void {
  const nodeId = (event.data as { nodeId?: unknown }).nodeId;
  const tool = (event.data as { tool?: unknown }).tool;
  const toolUseId = (event.data as { toolUseId?: unknown }).toolUseId;
  const status = (event.data as { status?: unknown }).status;
  const success = (event.data as { success?: unknown }).success;
  const summary = (event.data as { summary?: unknown }).summary;
  const args = (event.data as { args?: unknown }).args;
  const timestamp = (event.data as { timestamp?: unknown }).timestamp;
  if (typeof nodeId !== 'string' || typeof tool !== 'string') return;
  const resolvedStatus = status === 'completed' ? 'completed' : 'requested';
  const ts = typeof timestamp === 'string' ? timestamp : new Date().toISOString();
  const resolvedSuccess = typeof success === 'boolean' ? success : undefined;
  const resolvedSummary = typeof summary === 'string' ? summary : undefined;
  const resolvedArgs = typeof args === 'string' ? args : undefined;
  safeSetState((prev) => {
    const next = new Map(prev.perNode);
    // Same fallback as `handleNodeText`: tool envelopes can arrive
    // ahead of the first node_state, especially when the worker
    // emits the tool_request and the RUNNING transition in the same
    // tick. Seed a minimal entry so the dock chip is preserved.
    const existing = next.get(nodeId) ?? {
      status: 'running' as RunNodeStatus,
      iteration: 0,
      errorMessage: null,
      errorDetail: null,
      artifacts: [] as readonly ProcessArtifactDto[],
      transcript: [] as readonly TranscriptEntry[],
    };

    let updatedTranscript: readonly TranscriptEntry[];
    if (resolvedStatus === 'completed' && typeof toolUseId === 'string') {
      // Find the matching `requested` chip and promote it in place so the
      // spinner flips to a checkmark / cross without inserting a second
      // row. Required by the StatusPill UX: the dock shows one chip per
      // tool invocation across its lifecycle.
      const idx = existing.transcript.findIndex(
        (e) => e.type === 'tool' && e.toolUseId === toolUseId && e.status === 'requested',
      );
      if (idx >= 0) {
        // Completed envelopes usually carry no summary — keep the one from
        // the requested entry so the chip title doesn't lose it.
        updatedTranscript = existing.transcript.map((e, i) =>
          i === idx && e.type === 'tool'
            ? { ...e, status: 'completed' as const, success: resolvedSuccess, summary: resolvedSummary ?? e.summary, timestamp: ts }
            : e,
        );
      } else {
        // No matching requested entry: either the request was missed
        // (subscriber connected mid-flight) OR a duplicate `completed`
        // arrived for an entry we already promoted. Dedupe by
        // `(toolUseId, status)` so the same envelope replayed by the
        // fast-path / projector race doesn't add a second completed row.
        const alreadyCompleted = existing.transcript.some(
          (e) => e.type === 'tool' && e.toolUseId === toolUseId && e.status === 'completed',
        );
        updatedTranscript = alreadyCompleted
          ? existing.transcript
          : [...existing.transcript, { type: 'tool', tool, toolUseId, status: resolvedStatus, success: resolvedSuccess, summary: resolvedSummary, args: resolvedArgs, timestamp: ts }];
      }
    } else {
      // Same dedupe as `handleNodeText` (see comment there): drop exact
      // re-emissions caused by the fast-path / projector overlap or a
      // reconnect that re-reads from a slightly behind cursor.
      const isDup = existing.transcript.some(
        (e) => e.type === 'tool'
          && e.toolUseId === (typeof toolUseId === 'string' ? toolUseId : null)
          && e.status === resolvedStatus
          && e.timestamp === ts,
      );
      updatedTranscript = isDup
        ? existing.transcript
        : [...existing.transcript, { type: 'tool', tool, toolUseId: typeof toolUseId === 'string' ? toolUseId : null, status: resolvedStatus, success: resolvedSuccess, summary: resolvedSummary, args: resolvedArgs, timestamp: ts }];
    }

    next.set(nodeId, { ...existing, transcript: updatedTranscript });
    return { ...prev, perNode: next };
  });
}

/**
 * Build the SSE event handler for the run session. Extracted from the
 * hook to keep the hook body focused on orchestration and lifecycle.
 */
export function createEventHandler(deps: EventHandlerDeps) {
  return (event: SseEvent): void => {
    switch (event.type) {
      case 'heartbeat':
      case 'started':
      case 'chain_step':
        return;
      case 'reconnected':
        handleReconnected(event, deps.safeSetState);
        return;
      case 'run_status':
        handleRunStatus(event, deps);
        return;
      case 'node_state':
        handleNodeState(event, deps.safeSetState);
        return;
      case 'node_text':
        handleNodeText(event, deps.safeSetState);
        return;
      case 'node_tool':
        handleNodeTool(event, deps.safeSetState);
        return;
      case 'artifact':
        handleArtifact(event, deps.safeSetState);
        return;
      case 'gate_waiting':
        handleGateWaiting(event, deps.safeSetState);
        return;
      case 'gate_decided':
        handleGateDecided(event, deps);
        return;
      case 'stream_end':
        handleStreamEnd(deps);
        return;
      default:
        return;
    }
  };
}
